
import { useFormContext } from "react-hook-form";
import { ProductCardRadio } from "../ui/ProductCardRadio";
import { useOfferContext } from "@/lib/hooks/useOfferContext";
import { AdditionalQuestions } from "../ui/AdditionalQuestions";
import { ProductDetailsComponent } from "../ProductDetailsComponent";
import { useEffect, useMemo, useState } from "react";
import { ApiResponseV2, ErrorResponse, QuoteRequest, QuoteResponse } from "@vertical-insure/shared";
import { RiLoader4Line } from "@remixicon/react";
import {SelectNative} from "@/components/ui/SelectNative";
import { Controller } from "react-hook-form"
import {parseBadRequestErrorMessage} from "@/lib/utils";

export const additionalQuestions = {
  general_liability_loss_exceeded_max_loss: (<span>Has the event host had a general liability loss greater than $5,000?</span>),
  venue_is_residential_home_rental: (<span>Will the event be hosted at a residence or house rental property (i.e. AirBnB, VRBO or similar)?</span>),
  overnight_stay: (<span>Are your attendees staying overnight at the host venue?</span>),
  renting_entire_venue_location: (<span>If there is an overnight stay, is the entire venue location being rented out?</span>),
  late_night_event: (<span>Will your event involve camping or go past 2 AM?</span>),
  excluded_event_features: (
    <div>
      Does your event have any of the following?
      <ul className="text-xs opacity-80 list-disc ml-4 my-2 space-y-2">
        <li>Activities taking place in or on water (i.e. swimming)</li>
        <li>Animals (excluding service animals)</li>
        <li>Cannabis including but not limited to Edibles, CBD, etc.</li>
        <li>Celebrity Appearances and/or High Profile Attendees</li>
        <li>Firearms/Ammunition/Projectiles and/or weapons of any kind</li>
        <li>Motorsports</li>
        <li>Open Flames</li>
        <li>Rap, Hip Hop, Heavy Metal, Punk Rock and/or EDM Performances</li>
      </ul>
    </div>
  ),
}

const musicOptions = [{
  label: 'Choir',
  value: 'CHOIR'
}, {
  label: 'Classical',
  value: 'CLASSICAL'
}, {
  label: 'Doo-Wop',
  value: 'DOO_WOP'
}, {
  label: 'Jazz',
  value: 'JAZZ'
}, {
  label: 'Orchestra',
  value: 'ORCHESTRA'
}, {
  label: 'Opera',
  value: 'OPERA'
}, {
  label: 'Symphony',
  value: 'SYMPHONY'
}, {
  label: 'Folk Guitar',
  value: 'FOLK_GUITAR'
}, {
  label: 'Country',
  value: 'COUNTRY'
}, {
  label: 'Classic Rock',
  value: 'CLASSIC_ROCK'
}, {
  label: 'Disc Jockey',
  value: 'DISC_JOCKEY'
}, {
  label: 'Cover Band',
  value: 'COVER_BAND'
}, {
  label: 'Other',
  value: 'OTHER'
}]

export const EventHostLiabilityLayout = ({quote}: {quote: QuoteResponse | ErrorResponse | ApiResponseV2}) => {
  
  const { updateProductConfig, shouldDisplayLayout, config, loading } = useOfferContext();
  const { watch, getValues } = useFormContext();

  const [allQuestionsAnsweredInConfig, setAllQuestionsAnsweredInConfig] = useState<boolean | undefined>(undefined);
  const [allQuestionsAnsweredInForm, setAllQuestionsAnsweredInForm] = useState<boolean | undefined>(undefined);

  const quoteRequest = useMemo<QuoteRequest>(() => {
    return config?.product_config ? Object.values(config.product_config).flat()[0] : {}
  }, [config]);

  useEffect(() => {
    const { unsubscribe } = watch((values) => {
      const updatedStatus = Object.keys(additionalQuestions)
        .every(question => question in values && values[question] !== undefined)
        && values['feature_live_music'] !== undefined
        && ((values['feature_live_music'] && values['live_music_type'] !== undefined)
          || values['feature_live_music'] === false);

      if (updatedStatus !== allQuestionsAnsweredInForm) {
        setAllQuestionsAnsweredInForm(updatedStatus);
      }
    });

    return () => unsubscribe()
  }, [watch, allQuestionsAnsweredInForm])
  
  useEffect(() => {
    if (allQuestionsAnsweredInConfig === undefined && 'policy_attributes' in quoteRequest) {
      setAllQuestionsAnsweredInConfig(Object.keys(additionalQuestions).every(question => quoteRequest.policy_attributes && Object.keys(quoteRequest.policy_attributes).includes(question)));
    }
  }, [allQuestionsAnsweredInConfig, quoteRequest]);

  useEffect(() => {
    if (allQuestionsAnsweredInForm && 'policy_attributes' in quoteRequest) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { accepted_quotes, offer_declined, live_music_type, ...additionQuestionResponses } = getValues();
     
      const updatedQuoteRequest = {
        ...quoteRequest,
        policy_attributes: {
          ...quoteRequest.policy_attributes,
          ...additionQuestionResponses,
          ...(live_music_type === undefined || live_music_type === 'NOT_APPLICABLE' ? {} : {
            live_music_type
          })
        }
      }
      const updatedConfig = {
        'event-host-liability': [updatedQuoteRequest]
      }
      
      updateProductConfig(updatedConfig);
    }
  }, [allQuestionsAnsweredInForm]);

  const featureLiveMusic = watch("feature_live_music");

  // TODO We only want to show the additional questions form if the quote request came back as bad_request (meaning they didn't supply everything that was needed)
  // If we got a different status code / error code (like for underwriting) we will display that error message.

  const shouldHideQuestions = allQuestionsAnsweredInConfig || allQuestionsAnsweredInForm  || ('error_code' in quote && quote.error_code === 'not_eligible');

  return (
    <div id="offer">
      <div className={`border p-2 rounded space-y-2 ${shouldDisplayLayout ? 'hidden' : 'block'}`}>
        <ProductDetailsComponent />
        {loading && 
          <div className="w-full flex items-center justify-center">
            <RiLoader4Line className="size-8 animate-spin" />
          </div>
        }
        {('error_code' in quote && quote.error_code === 'not_eligible') &&
          <div className="text-sm text-error">
            {quote.error}
          </div>
        }

        {shouldHideQuestions && !loading && ('error_code' in quote && quote.error_code === 'bad_request') &&
          <div className="text-sm text-error">
            We are unable to quote your event at this time: <span className="font-medium">{parseBadRequestErrorMessage(quote.error)}</span>
          </div>
        }
        <div className={shouldHideQuestions ? 'hidden' : 'block'}>

          <Controller name="feature_live_music" defaultValue={shouldHideQuestions ? false : undefined} render={({field}) => (
            <div>
              <div className="text-sm font-semibold">Will your event feature live music?</div>
              <div className="flex space-x-4 ml-2 mb-4">
                <label className={`flex gap-2 cursor-pointer py-1 text-sm sm:items-center`}>
                  <input {...field} value="yes" type="radio" checked={field.value === true} onChange={() => field.onChange(true)}/> Yes
                </label>
                <label className={`flex gap-2 cursor-pointer py-1 text-sm sm:items-center`}>
                  <input {...field} value="no" type="radio" checked={field.value === false} onChange={() => field.onChange(false)}/> No
                </label>
              </div>
            </div>
          )}/>

          {featureLiveMusic &&
            <Controller name="live_music_type" render={({field}) => (
              <div className="flex flex-col mb-4">
                <label className="text-sm font-semibold" htmlFor="live_music_type">What is the type of music?</label>
                <SelectNative id="live_music_type" placeholder="Select music type" {...field}>
                  <option>Select music type</option>
                  {musicOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </SelectNative>
              </div>
            )}/>
          }
          <AdditionalQuestions quoteRequest={quoteRequest} additionalQuestions={additionalQuestions} />

        </div>
      </div>
      
      {shouldDisplayLayout && 
        <ProductCardRadio quote={quote} product="event-host-liability" />
      }
    </div>
  );
}